import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Table, Input, Row, Col, Button, Modal, ModalHeader, ModalBody, ModalFooter, Label } from "reactstrap";
import { FaDatabase, FaLeaf } from "react-icons/fa";
import adminService from "../../services/adminService";
import notification from "../../utils/notification";
import Loader from "../../components/loader";
import "../../assets/scss/pages/admin/_anonymousDataMigration.scss";

interface AnonymousDataItem {
  id: string;
  name: string;
  code: string;
  cost: number;
  country: string;
  status: "pending" | "moved_to_products" | "moved_to_ingredients";
  is_selectable: boolean;
  is_audited?: boolean;
  is_migrated?: boolean;
  is_guava?: boolean;
  updated_at?: string;
  ip_address?: string;
}

interface ApiResponse {
  success: boolean;
  data: AnonymousDataItem[];
  pagination: {
    current_page: number;
    per_page: number;
    has_next: boolean;
    has_prev: boolean;
    estimated_total: string | number;
  };
}

interface MigrationPreviewItem {
  id: string;
  original_data: Record<string, any>;
  mapped_data: Record<string, any>;
  missing_fields: string[];
  validation_errors: string[];
}

interface MigrationPreviewResponse {
  success: boolean;
  data: {
    items: MigrationPreviewItem[];
    total_items: number;
  };
}

interface MigrationPreview {
  items: MigrationPreviewItem[];
  targetType: "products" | "ingredients";
  totalItems: number;
}

interface EditableFormData {
  [itemId: string]: {
    // Required Fields
    name: string;
    version: number;
    
    // Optional Fields
    code?: string;
    product_image?: string;
    standard_of_identity?: string;
    format?: string;
    flavor?: string;
    product_class?: string;
    product_form?: string;
    serving_size?: number;
    unit_serving_size?: string;
    cost?: number;
    markup?: number;
    cost_margin?: number;
    weight?: string;
    functional_tags?: string;
    country?: string;
    notes?: string;
    allergens?: string;
    certifications?: string;
    unit?: string;
    nutrition_score?: number;
    sustainability_score?: number;
    recipe_type?: string;
    is_guava?: boolean;
    is_ai_generated?: boolean;
  };
}

interface PaginationInfo {
  page: number;
  size: number;
  hasNext: boolean;
  hasPrev: boolean;
  estimatedTotal: string | number;
}

const ITEMS_PER_PAGE = 25;

const AnonymousDataMigration: React.FC = () => {
  const dispatch = useDispatch();
  
  // Data state
  const [data, setData] = useState<AnonymousDataItem[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    size: ITEMS_PER_PAGE,
    hasNext: false,
    hasPrev: false,
    estimatedTotal: 0
  });
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Modal state
  const [previewModal, setPreviewModal] = useState(false);
  const [migrationPreview, setMigrationPreview] = useState<MigrationPreview | null>(null);
  const [migrating, setMigrating] = useState(false);
  const [editableData, setEditableData] = useState<EditableFormData>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, Record<string, string>>>({});
  
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch data
  const fetchData = async (page = 1) => {
    try {
      setLoading(true);
      
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      abortControllerRef.current = new AbortController();
      
      // Build params object
      const params: any = {
        page,
        limit: ITEMS_PER_PAGE,
        status: "all"
      };
      
      console.log('API Request params:', params); // Debug log
      
      const response: ApiResponse = await adminService.getAnonymousData(params, abortControllerRef.current.signal);
      
      if (response.success) {
        setData(response.data);
        setPagination({
          page: response.pagination?.current_page || 1,
          size: response.pagination?.per_page || ITEMS_PER_PAGE,
          hasNext: response.pagination?.has_next || false,
          hasPrev: response.pagination?.has_prev || false,
          estimatedTotal: response.pagination?.estimated_total || 0
        });
      } else {
        throw new Error("API returned success: false");
      }
      
      // Reset selections when data changes
      setSelectedItems(new Set());
      setSelectAll(false);
      
    } catch (error) {
      const err = error as any;
      if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') {
        notification.error("Failed to fetch anonymous data");
      }
    } finally {
      setLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    fetchData(1);
  }, []);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Handlers
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1) {
      fetchData(newPage);
    }
  };

  const handleItemSelect = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
    setSelectAll(newSelected.size === data.filter(item => item.is_selectable).length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems(new Set());
    } else {
      const selectableItems = data.filter(item => item.is_selectable).map(item => item.id);
      setSelectedItems(new Set(selectableItems));
    }
    setSelectAll(!selectAll);
  };

  const handleMigrationPreview = async (targetType: "products" | "ingredients") => {
    if (selectedItems.size === 0) {
      notification.info("Please select items to migrate");
      return;
    }

    try {
      setLoading(true);
      
      const selectedIds = Array.from(selectedItems);
      const targetTable = targetType === "products" ? "product_list" : "ingredients_list";
      
      const response: MigrationPreviewResponse = await adminService.previewMigration({
        selected_ids: selectedIds,
        target_table: targetTable
      });

      if (response.success) {
        setMigrationPreview({
          items: response.data.items,
          targetType,
          totalItems: response.data.total_items
        });
        
        // Initialize editable data with mapped data
        const initialEditableData: EditableFormData = {};
        response.data.items.forEach(item => {
          initialEditableData[item.id] = {
            // Required fields
            name: item.mapped_data.name || '',
            version: item.mapped_data.version || 1,
            
            // Optional fields
            code: item.mapped_data.code || '',
            product_image: item.mapped_data.product_image || '',
            standard_of_identity: item.mapped_data.standard_of_identity || '',
            format: item.mapped_data.format || '',
            flavor: item.mapped_data.flavor || '',
            product_class: item.mapped_data.product_class || '',
            product_form: item.mapped_data.product_form || '',
            serving_size: item.mapped_data.serving_size || 0,
            unit_serving_size: item.mapped_data.unit_serving_size || '',
            cost: item.mapped_data.cost || 0,
            markup: item.mapped_data.markup || 0,
            cost_margin: item.mapped_data.cost_margin || 0,
            weight: item.mapped_data.weight || '',
            functional_tags: item.mapped_data.functional_tags || '',
            country: item.mapped_data.country || '',
            notes: item.mapped_data.notes || '',
            allergens: Array.isArray(item.mapped_data.allergens) ? item.mapped_data.allergens.join(', ') : '',
            certifications: Array.isArray(item.mapped_data.certifications) ? item.mapped_data.certifications.join(', ') : '',
            unit: item.mapped_data.unit || '',
            nutrition_score: item.mapped_data.nutrition_score || 0,
            sustainability_score: item.mapped_data.sustainability_score || 0,
            recipe_type: item.mapped_data.recipe_type || '',
            is_guava: item.mapped_data.is_guava || false,
            is_ai_generated: item.mapped_data.is_ai_generated || false
          };
        });
        
        setEditableData(initialEditableData);
        setValidationErrors({});
        setPreviewModal(true);
      } else {
        throw new Error("Preview migration failed");
      }
    } catch (error) {
      notification.error("Failed to generate migration preview");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmMigration = async () => {
    if (!migrationPreview) return;

    // Validate required fields
    const errors: Record<string, Record<string, string>> = {};
    let hasErrors = false;

    migrationPreview.items.forEach(item => {
      const itemData = editableData[item.id];
      const itemErrors: Record<string, string> = {};

      if (!itemData.name?.trim()) {
        itemErrors.name = "Name is required";
        hasErrors = true;
      }
      if (!itemData.version || itemData.version < 1) {
        itemErrors.version = "Version must be a positive number";
        hasErrors = true;
      }

      if (Object.keys(itemErrors).length > 0) {
        errors[item.id] = itemErrors;
      }
    });

    if (hasErrors) {
      setValidationErrors(errors);
      notification.error("Please fix validation errors before proceeding");
      return;
    }

    try {
      setMigrating(true);
      
      // Prepare migration data with user edits
      const additionalData: Record<string, any> = {};
      migrationPreview.items.forEach(item => {
        const itemData = editableData[item.id];
        // Only include fields that have values (not empty strings or default values)
        const cleanedData: Record<string, any> = {};
        
        Object.entries(itemData).forEach(([key, value]) => {
          if (value !== '' && value !== null && value !== undefined) {
            // For boolean fields, include them even if false
            if (typeof value === 'boolean') {
              cleanedData[key] = value;
            }
            // For numbers, include if not 0 (except version which is required)
            else if (typeof value === 'number' && (value !== 0 || key === 'version')) {
              cleanedData[key] = value;
            }
            // For strings, include if not empty
            else if (typeof value === 'string' && value.trim() !== '') {
              // Convert comma-separated strings to arrays for allergens and certifications
              if (key === 'allergens' || key === 'certifications') {
                cleanedData[key] = value.split(',').map(item => item.trim()).filter(item => item);
              } else {
                cleanedData[key] = value;
              }
            }
          }
        });
        
        additionalData[item.id] = cleanedData;
      });
      
      await adminService.performMigration(migrationPreview.targetType, {
        selected_ids: migrationPreview.items.map(item => item.id),
        additional_data: additionalData
      });

      notification.info(`Successfully migrated ${migrationPreview.totalItems} items to ${migrationPreview.targetType}`);
      
      // Close modal and refresh data
      setPreviewModal(false);
      setMigrationPreview(null);
      setEditableData({});
      setValidationErrors({});
      setSelectedItems(new Set());
      setSelectAll(false);
      fetchData(pagination.page);
      
    } catch (error) {
      notification.error("Migration failed. Please try again.");
    } finally {
      setMigrating(false);
    }
  };

  const handleFieldChange = (itemId: string, fieldName: string, value: any) => {
    setEditableData(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [fieldName]: value
      }
    }));

    // Clear validation error for this field
    if (validationErrors[itemId]?.[fieldName]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors[itemId]) {
          const { [fieldName]: removed, ...rest } = newErrors[itemId];
          newErrors[itemId] = rest;
        }
        return newErrors;
      });
    }
  };



  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const selectedCount = selectedItems.size;
  const canMigrate = selectedCount > 0;

  return (
    <div className="anonymous-data-migration-wrapper">
      <div className="anonymous-data-migration">
        {/* Header */}
        <div className="admin-data-container__header">
          <div className="header-content">
            <div className="header-text">
              <h3 className="page-title">
                <i className="fas fa-database me-2"></i>
                Anonymous Data Migration
              </h3>
              <p className="page-description">
                Migrate anonymous data entries to Products or Ingredients. Select items and choose target destination.
              </p>
            </div>
            {selectedCount > 0 && (
              <div className="selection-summary">
                <div className="selection-badge">
                  <i className="fas fa-check-circle me-2"></i>
                  <span className="selection-count">{selectedCount}</span>
                  <span className="selection-text">item{selectedCount > 1 ? 's' : ''} selected</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons-container">
          <div className="action-buttons">
            <Button
              color="primary"
              disabled={!canMigrate}
              onClick={() => handleMigrationPreview("products")}
              className="action-btn action-btn-primary"
            >
              <FaDatabase className="btn-icon" />
              <span className="btn-text">Move to Products</span>
              {selectedCount > 0 && <span className="btn-count">({selectedCount})</span>}
            </Button>
            <Button
              color="success"
              disabled={!canMigrate}
              onClick={() => handleMigrationPreview("ingredients")}
              className="action-btn action-btn-success"
            >
              <FaLeaf className="btn-icon" />
              <span className="btn-text">Move to Ingredients</span>
              {selectedCount > 0 && <span className="btn-count">({selectedCount})</span>}
            </Button>
          </div>
          {!canMigrate && (
            <div className="action-hint">
              <i className="fas fa-info-circle me-2"></i>
              Select items from the table below to enable migration actions
            </div>
          )}
        </div>



        {/* Data Table */}
        <div className="table-container">
          {loading ? (
            <div className="text-center py-5">
              <Loader isLoading={true} />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="migration-table" style={{ minWidth: "1600px" }}>
                  <thead>
                    <tr>
                      <th className="checkbox-column">
                        <div className="custom-checkbox">
                          <Input
                            type="checkbox"
                            id="select-all"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            disabled={data.filter(item => item.is_selectable).length === 0}
                          />
                          <label htmlFor="select-all" className="checkbox-label"></label>
                        </div>
                      </th>
                      <th className="name-column">Name</th>
                      <th className="code-column">Code</th>
                      <th className="cost-column">Cost</th>
                      <th className="country-column">Country</th>
                      <th className="audit-column text-center">Audit Status</th>
                      <th className="migration-column text-center">Migration Status</th>
                      <th className="guava-column text-center">Guava Type</th>
                      <th className="date-column">Date of Update</th>
                      <th className="ip-column">IP Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="text-center py-4 no-data">
                          <div className="no-data-content">
                            <i className="fas fa-inbox fa-2x text-muted mb-3"></i>
                            <p className="mb-0 text-muted">No data found</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      data.map((item) => {
                        // Helper to safely check boolean values (handles true, "true", 1, "1")
                        const getBoolean = (val: any) => {
                          if (val === true || val === "true" || val === 1 || val === "1") return true;
                          return false;
                        };

                        const isAudited = getBoolean(item.is_audited);
                        const isMigrated = getBoolean(item.is_migrated);
                        const isGuava = getBoolean(item.is_guava);

                        return (
                          <tr
                            key={item.id}
                            className={`data-row ${!item.is_selectable ? "disabled-row" : ""}`}
                          >
                            <td className="checkbox-column">
                              <div className="custom-checkbox">
                                <Input
                                  type="checkbox"
                                  id={`item-${item.id}`}
                                  checked={selectedItems.has(item.id)}
                                  onChange={() => handleItemSelect(item.id)}
                                  disabled={!item.is_selectable}
                                />
                                <label htmlFor={`item-${item.id}`} className="checkbox-label"></label>
                              </div>
                            </td>
                            <td className="name-column">
                              <div className="cell-content">
                                <span className="item-name" title={item.name}>{item.name}</span>
                              </div>
                            </td>
                            <td className="code-column">
                              <div className="cell-content">
                                <code className="item-code">{item.code}</code>
                              </div>
                            </td>
                            <td className="cost-column">
                              <div className="cell-content">
                                <span className="item-cost">{formatCurrency(item.cost)}</span>
                              </div>
                            </td>
                            <td className="country-column">
                              <div className="cell-content">
                                <span className="item-country">{item.country}</span>
                              </div>
                            </td>
                            <td className="audit-column text-center">
                              {isAudited ? (
                                <span className="text-success">Audited</span>
                              ) : (
                                <span className="text-muted">Not Audited</span>
                              )}
                            </td>
                            <td className="migration-column text-center">
                              {isMigrated ? (
                                <span className="text-success">Migrated</span>
                              ) : (
                                <span className="text-warning">Pending</span>
                              )}
                            </td>
                            <td className="guava-column text-center">
                              {isGuava ? (
                                <span className="text-primary font-weight-bold">Yes</span>
                              ) : (
                                "False"
                              )}
                            </td>
                            <td className="date-column">
                              {item.updated_at ? new Date(item.updated_at).toLocaleString() : "-"}
                            </td>
                            <td className="ip-column">{item.ip_address || "-"}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="pagination-container">
                <div className="pagination-info">
                  <span className="showing-text">Showing</span>
                  <span className="range-text">
                    {((pagination.page - 1) * pagination.size) + 1} to {((pagination.page - 1) * pagination.size) + data.length}
                  </span>
                  <span className="total-text">
                    of {pagination.estimatedTotal} entries
                  </span>
                </div>
                <div className="pagination-controls">
                  <Button
                    color="light"
                    size="sm"
                    disabled={!pagination.hasPrev}
                    onClick={() => handlePageChange(pagination.page - 1)}
                    className="pagination-btn"
                  >
                    <i className="fas fa-chevron-left me-1"></i>
                    Previous
                  </Button>
                  <span className="page-info">
                    Page {pagination.page}
                  </span>
                  <Button
                    color="light"
                    size="sm"
                    disabled={!pagination.hasNext}
                    onClick={() => handlePageChange(pagination.page + 1)}
                    className="pagination-btn"
                  >
                    Next
                    <i className="fas fa-chevron-right ms-1"></i>
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Migration Preview Modal */}
        <Modal isOpen={previewModal} toggle={() => setPreviewModal(false)} size="xl" className="migration-preview-modal">
          <ModalHeader toggle={() => setPreviewModal(false)} className="modal-header-custom">
            <div className="modal-title-container">
              <div className="modal-icon">
                {migrationPreview?.targetType === "products" ? (
                  <FaDatabase className="icon-large" />
                ) : (
                  <FaLeaf className="icon-large" />
                )}
              </div>
              <div className="modal-title-text">
                <h4 className="modal-title-main">Migration Preview</h4>
                <p className="modal-subtitle">
                  Moving to {migrationPreview?.targetType === "products" ? "Products" : "Ingredients"}
                </p>
              </div>
            </div>
          </ModalHeader>
          <ModalBody className="modal-body-custom">
            {migrationPreview && (
              <>
                {/* Summary Card */}
                <div className="migration-summary-card">
                  <div className="summary-header">
                    <div className="summary-icon">
                      <i className="fas fa-chart-bar"></i>
                    </div>
                    <div className="summary-content">
                      <h5 className="summary-title">Migration Summary</h5>
                      <p className="summary-description">
                        Review and edit the data before confirming migration
                      </p>
                    </div>
                    <div className="summary-stats">
                      <div className="stat-item">
                        <span className="stat-number">{migrationPreview.totalItems}</span>
                        <span className="stat-label">Items</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Items Preview */}
                <div className="migration-preview-items">
                  {migrationPreview.items.map((item, index) => {
                    const itemData = editableData[item.id] || {};
                    const itemErrors = validationErrors[item.id] || {};

                    return (
                      <div key={item.id} className="preview-item-card">
                        {/* Item Header */}
                        <div className="item-header-section">
                          <div className="item-number">
                            <span className="number-badge">{index + 1}</span>
                          </div>
                          <div className="item-info">
                            <h6 className="item-name">{item.original_data.name}</h6>
                            <p className="item-code">Code: {item.original_data.code || 'N/A'}</p>
                          </div>
                          <div className="item-status">
                            <span className="status-badge status-success">
                              <i className="fas fa-check-circle"></i>
                              Ready
                            </span>
                          </div>
                        </div>

                        {/* Editable Form */}
                        <div className="editable-form-container">
                          {/* Required Fields Section */}
                          <div className="form-section required-section">
                            <div className="section-header">
                              <div className="section-icon required-icon">
                                <i className="fas fa-asterisk"></i>
                              </div>
                              <h6 className="section-title">Required Fields</h6>
                            </div>
                            <div className="form-grid">
                              <div className="form-group">
                                <label className="form-label required">Name</label>
                                <Input
                                  type="text"
                                  value={itemData.name || ''}
                                  onChange={(e) => handleFieldChange(item.id, 'name', e.target.value)}
                                  invalid={!!itemErrors.name}
                                  className="form-control-custom"
                                  placeholder="Enter product/ingredient name"
                                />
                                {itemErrors.name && (
                                  <div className="error-message">
                                    <i className="fas fa-exclamation-circle"></i>
                                    {itemErrors.name}
                                  </div>
                                )}
                              </div>

                              <div className="form-group">
                                <label className="form-label required">Version</label>
                                <Input
                                  type="number"
                                  value={itemData.version || 1}
                                  onChange={(e) => handleFieldChange(item.id, 'version', parseInt(e.target.value) || 1)}
                                  invalid={!!itemErrors.version}
                                  className="form-control-custom"
                                  min="1"
                                  placeholder="1"
                                />
                                {itemErrors.version && (
                                  <div className="error-message">
                                    <i className="fas fa-exclamation-circle"></i>
                                    {itemErrors.version}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Optional Fields Section */}
                          <div className="form-section optional-section">
                            <div className="section-header">
                              <div className="section-icon optional-icon">
                                <i className="fas fa-cog"></i>
                              </div>
                              <h6 className="section-title">Optional Fields</h6>
                              <button
                                type="button"
                                className="section-toggle"
                                onClick={() => {
                                  const section = document.querySelector(`#optional-${item.id}`);
                                  section?.classList.toggle('collapsed');
                                }}
                              >
                                <i className="fas fa-chevron-down"></i>
                              </button>
                            </div>
                            <div id={`optional-${item.id}`} className="form-grid collapsible-content">
                              <div className="form-group">
                                <label className="form-label">Code</label>
                                <Input
                                  type="text"
                                  value={itemData.code || ''}
                                  onChange={(e) => handleFieldChange(item.id, 'code', e.target.value)}
                                  className="form-control-custom"
                                  placeholder="Product/ingredient code"
                                />
                              </div>

                              <div className="form-group">
                                <label className="form-label">Product Class</label>
                                <Input
                                  type="text"
                                  value={itemData.product_class || ''}
                                  onChange={(e) => handleFieldChange(item.id, 'product_class', e.target.value)}
                                  className="form-control-custom"
                                  placeholder="Classification"
                                />
                              </div>

                              <div className="form-group">
                                <label className="form-label">Format</label>
                                <Input
                                  type="text"
                                  value={itemData.format || ''}
                                  onChange={(e) => handleFieldChange(item.id, 'format', e.target.value)}
                                  className="form-control-custom"
                                  placeholder="Product format"
                                />
                              </div>

                              <div className="form-group">
                                <label className="form-label">Flavor</label>
                                <Input
                                  type="text"
                                  value={itemData.flavor || ''}
                                  onChange={(e) => handleFieldChange(item.id, 'flavor', e.target.value)}
                                  className="form-control-custom"
                                  placeholder="Flavor profile"
                                />
                              </div>

                              <div className="form-group">
                                <label className="form-label">Product Form</label>
                                <Input
                                  type="text"
                                  value={itemData.product_form || ''}
                                  onChange={(e) => handleFieldChange(item.id, 'product_form', e.target.value)}
                                  className="form-control-custom"
                                  placeholder="Physical form"
                                />
                              </div>

                              <div className="form-group">
                                <label className="form-label">Serving Size</label>
                                <Input
                                  type="number"
                                  value={itemData.serving_size || ''}
                                  onChange={(e) => handleFieldChange(item.id, 'serving_size', parseFloat(e.target.value) || 0)}
                                  className="form-control-custom"
                                  step="0.01"
                                  placeholder="0.00"
                                />
                              </div>

                              <div className="form-group">
                                <label className="form-label">Unit Serving Size</label>
                                <Input
                                  type="text"
                                  value={itemData.unit_serving_size || ''}
                                  onChange={(e) => handleFieldChange(item.id, 'unit_serving_size', e.target.value)}
                                  className="form-control-custom"
                                  placeholder="e.g., grams, ml"
                                />
                              </div>

                              <div className="form-group">
                                <label className="form-label">Cost</label>
                                <div className="input-with-icon">
                                  <i className="fas fa-dollar-sign input-icon"></i>
                                  <Input
                                    type="number"
                                    value={itemData.cost || ''}
                                    onChange={(e) => handleFieldChange(item.id, 'cost', parseFloat(e.target.value) || 0)}
                                    className="form-control-custom with-icon"
                                    step="0.01"
                                    placeholder="0.00"
                                  />
                                </div>
                              </div>

                              <div className="form-group">
                                <label className="form-label">Weight</label>
                                <Input
                                  type="text"
                                  value={itemData.weight || ''}
                                  onChange={(e) => handleFieldChange(item.id, 'weight', e.target.value)}
                                  className="form-control-custom"
                                  placeholder="Weight specification"
                                />
                              </div>

                              <div className="form-group">
                                <label className="form-label">Country</label>
                                <Input
                                  type="text"
                                  value={itemData.country || ''}
                                  onChange={(e) => handleFieldChange(item.id, 'country', e.target.value)}
                                  className="form-control-custom"
                                  placeholder="Country of origin"
                                />
                              </div>

                              <div className="form-group">
                                <label className="form-label">Unit</label>
                                <Input
                                  type="text"
                                  value={itemData.unit || ''}
                                  onChange={(e) => handleFieldChange(item.id, 'unit', e.target.value)}
                                  className="form-control-custom"
                                  placeholder="Measurement unit"
                                />
                              </div>

                              <div className="form-group">
                                <label className="form-label">Nutrition Score</label>
                                <div className="score-input">
                                  <Input
                                    type="number"
                                    value={itemData.nutrition_score || ''}
                                    onChange={(e) => handleFieldChange(item.id, 'nutrition_score', parseFloat(e.target.value) || 0)}
                                    className="form-control-custom"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    placeholder="0-100"
                                  />
                                  <span className="score-suffix">/ 100</span>
                                </div>
                              </div>

                              <div className="form-group">
                                <label className="form-label">Sustainability Score</label>
                                <div className="score-input">
                                  <Input
                                    type="number"
                                    value={itemData.sustainability_score || ''}
                                    onChange={(e) => handleFieldChange(item.id, 'sustainability_score', parseFloat(e.target.value) || 0)}
                                    className="form-control-custom"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    placeholder="0-100"
                                  />
                                  <span className="score-suffix">/ 100</span>
                                </div>
                              </div>

                              <div className="form-group">
                                <label className="form-label">Recipe Type</label>
                                <Input
                                  type="select"
                                  value={itemData.recipe_type || ''}
                                  onChange={(e) => handleFieldChange(item.id, 'recipe_type', e.target.value)}
                                  className="form-control-custom"
                                >
                                  <option value="">Select Recipe Type</option>
                                  <option value="formulation">Formulation</option>
                                  <option value="recipe">Recipe</option>
                                  <option value="blend">Blend</option>
                                </Input>
                              </div>

                              <div className="form-group full-width">
                                <label className="form-label">Allergens</label>
                                <Input
                                  type="text"
                                  value={itemData.allergens || ''}
                                  onChange={(e) => handleFieldChange(item.id, 'allergens', e.target.value)}
                                  className="form-control-custom"
                                  placeholder="e.g., milk, eggs, nuts (comma-separated)"
                                />
                              </div>

                              <div className="form-group full-width">
                                <label className="form-label">Certifications</label>
                                <Input
                                  type="text"
                                  value={itemData.certifications || ''}
                                  onChange={(e) => handleFieldChange(item.id, 'certifications', e.target.value)}
                                  className="form-control-custom"
                                  placeholder="e.g., organic, non-gmo, kosher (comma-separated)"
                                />
                              </div>

                              <div className="form-group full-width">
                                <label className="form-label">Notes</label>
                                <Input
                                  type="textarea"
                                  value={itemData.notes || ''}
                                  onChange={(e) => handleFieldChange(item.id, 'notes', e.target.value)}
                                  className="form-control-custom"
                                  rows="3"
                                  placeholder="Additional notes or comments"
                                />
                              </div>

                              <div className="form-group checkbox-group">
                                <div className="custom-checkbox-wrapper">
                                  <Input
                                    type="checkbox"
                                    id={`is_guava-${item.id}`}
                                    checked={itemData.is_guava || false}
                                    onChange={(e) => handleFieldChange(item.id, 'is_guava', e.target.checked)}
                                    className="custom-checkbox-input"
                                  />
                                  <label htmlFor={`is_guava-${item.id}`} className="custom-checkbox-label">
                                    <span className="checkbox-indicator"></span>
                                    <span className="checkbox-text">Is Guava Product</span>
                                  </label>
                                </div>
                              </div>

                              <div className="form-group checkbox-group">
                                <div className="custom-checkbox-wrapper">
                                  <Input
                                    type="checkbox"
                                    id={`is_ai_generated-${item.id}`}
                                    checked={itemData.is_ai_generated || false}
                                    onChange={(e) => handleFieldChange(item.id, 'is_ai_generated', e.target.checked)}
                                    className="custom-checkbox-input"
                                  />
                                  <label htmlFor={`is_ai_generated-${item.id}`} className="custom-checkbox-label">
                                    <span className="checkbox-indicator"></span>
                                    <span className="checkbox-text">Is AI Generated</span>
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </ModalBody>
          <ModalFooter className="modal-footer-custom">
            <div className="footer-content">
              {/* <div className="footer-info">
              <i className="fas fa-info-circle"></i>
              <span>Review all fields before confirming migration</span>
            </div> */}
              <div className="footer-actions">
                <Button
                  color="secondary"
                  onClick={() => setPreviewModal(false)}
                  className="btn-cancel"
                >
                  <i className="fas fa-times me-2"></i>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onClick={handleConfirmMigration}
                  disabled={migrating}
                  className="btn-confirm"
                >
                  {migrating ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-2"></i>
                      Migrating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check me-2"></i>
                      Confirm Migration
                    </>
                  )}
                </Button>
              </div>
            </div>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default AnonymousDataMigration;